import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import Button from "components/form/flat-button";
import Dialog from "components/dialog";
import { format } from "lib/globalize-util";
import { RadioButtonGroup, RadioButton } from "material-ui/RadioButton";
import Checkbox from "material-ui/Checkbox";
import MenuItem from "material-ui/MenuItem";
import theme from "styles/theme";
import Label from "components/form/label";
import Ajax from "lib/ajax";
import me from "models/me";
import Model from "lib/model";
import fetch from "lib/fetch";
import SelectField from "components/form/select-field";
import { Map, Set } from "immutable";
import { Form } from "formsy-react";
import ATSForm from "components/ats-form";
import memoize from "lodash/memoize";
import { DATATEST } from "constants/integration";
import ExportInitiationResult, { ExportType } from "components/export-initiation-result";
import { makeCreateAtsExportRequestPayload } from "helpers/ats-exports";
import { AtsExportPreset } from "constants/ats-exports";

const styles = {
    content: {
        maxWidth: "360px",
    },
    inputContainer: {
        margin: "16px 0 0",
    },
    export: {
        margin: "0 8px 12px 0",
    },
    radio: {
        display: "inline-block",
        width: "100%",
    },
    select: {
        width: "100%",
        margin: "24px 0 0",
    },
    exportFailure: {
        backgroundColor: theme.palette.warning,
        border: `1px solid ${theme.palette.error}`,
        padding: "6px",
    },
    checkbox: {
        margin: "30px 0 0 0",
    },
};

class ExportItems extends PureComponent {
    static contextTypes = {
        me: PropTypes.object.isRequired,
        muiTheme: PropTypes.object.isRequired,
    };

    constructor(props) {
        super(props);

        let orderFormTemplate = "horizontalqty";
        let atsCustomTemplate = "ats_report_csv";
        if (props.templates && props.templates.length) {
            const defaultTemplate = props.templates.find((template) => template.default_format);

            if (defaultTemplate) {
                orderFormTemplate = defaultTemplate.id;
            }
        }
        if (props.atsCustomTemplates && props.atsCustomTemplates.length) {
            const defaultTemplate = props.atsCustomTemplates.find((template) => template.default_format);
            atsCustomTemplate = defaultTemplate ? defaultTemplate.id : atsCustomTemplate;
        }

        const defaultQty = parseInt(props.activeBrand.getPreference("default_ats_min_qty"), 10);
        const minQuantity = !isNaN(defaultQty) ? defaultQty : "";

        this.state = {
            fileType: "csv",
            view: "vertical",
            archived: false,
            availFromDate: null,
            selectedWarehouses: Set(),
            warehouses: Map(),
            orderFormTemplate,
            atsCustomTemplate,
            minQuantity,
            excludeBrokenRange: false,
            exportStarted: false,
            images: false,
            atsOnly: false,
        };
    }

    UNSAFE_componentWillMount() {
        this.reset();
        // Hack: todo move to reducer
        if (process.env.NODE_ENV === "test") {
            return;
        }
        fetch("/api/v3.0/warehouses")
            .then((response) => response.json())
            .then((data) => {
                const warehouseData = data.length ? data : [];
                const warehouses = Map(
                    warehouseData.map((warehouse) => [warehouse.id, warehouse.display_name || warehouse.code])
                );
                if (!this._unmounted) {
                    this.setState({
                        warehouses,
                        selectedWarehouses: Set(data.length ? [data[0].id] : []),
                    });
                }
            });
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.open !== this.props.open && !nextProps.open) {
            this.reset();
        }

        if (nextProps.templates && nextProps.templates !== this.props.templates) {
            const defaultTemplate = nextProps.templates.find((template) => template.default_format);
            if (defaultTemplate) {
                this.setState({
                    orderFormTemplate: defaultTemplate.id,
                });
            }
        }
    }

    componentWillUnmount() {
        this._unmounted = true;
    }

    handleToggleArchived = (e, archived) => {
        e.stopPropagation();
        this.setState({
            archived,
        });
    };

    handleToggleImages = (e, images) => {
        e.stopPropagation();
        this.setState({
            images,
        });
    };

    handleExportButtonClicked = () => {
        const { me } = this.context;
        const { fileType, view, archived } = this.state;

        this.setState({
            exportStarted: true,
        });

        this.createItemExport();

        me.trackAction("Export items", {
            fileType,
            view,
            archived,
        });
    };

    handleRadioChange(attr, e, value) {
        const nextState = {};
        nextState[attr] = value;
        this.setState(nextState);
    }

    getExportSettings() {
        const { me } = this.context;
        const { filters, searchTerm, bulkItems, entities, type, viewAs, sortOrder, sortBy } = this.props;
        const { images, fileType, view, archived, orderFormTemplate } = this.state;

        const data = (filters || Map()).toJS();

        const active = data.__active || "active";
        delete data.__active;
        switch (active) {
            case "all":
                data.__view_disabled = true;
                break;
            case "inactive":
                data.__view_disabled = true;
                data.__active = false;
                break;
            case "active":
            default:
                data.__active = true;
                data.__view_disabled = false;
        }

        data.__archived = archived;
        if (archived) {
            data.__view_disabled = true;
        }

        if (searchTerm) {
            data.__search = searchTerm;
        }

        if (fileType === "orderForm") {
            data.__format = orderFormTemplate;
            const { availFromDate, selectedWarehouses, minQuantity, excludeBrokenRange, atsOnly } = this.state;

            Object.assign(data, {
                __avail_from_date: availFromDate && +availFromDate,
                __warehouses: selectedWarehouses.toArray(),
                __min_quantity: availFromDate && minQuantity, // do not use default val
                __exclude_broken_range: excludeBrokenRange,
                __ats_only: atsOnly,
            });
        } else {
            data.__format = view;
        }
        data.__file_type = fileType;

        Object.assign(
            data,
            bulkItems.toFilters((id) => (type === "item" ? id : entities.entries[id].item_id))
        );

        if (viewAs) {
            data.__company = viewAs._id;
        }

        data.__currency_code = me._activeCurrency;
        data.__msrp_currency_code = me._activeMSRPCurrency;
        if (data.__file_type === "xlsx") {
            data.__images = images;
        }

        data.__sort = sortBy;
        data.__sort_order = sortOrder;

        return data;
    }

    getAtsExportRequestPayload() {
        const { type, entities, bulkItems, filters: inputFilters, viewAs, sortBy: sort, sortOrder } = this.props;
        const { atsCustomTemplate } = this.state;

        const payload = makeCreateAtsExportRequestPayload(atsCustomTemplate);
        payload.items_options = {
            filters: bulkItems.toFilters((id) => (type === "item" ? id : entities.entries[id].item_id)),
            sort,
            sortOrder,
        };
        if (this.props.activeBrand.hasPreference("separate_msrp_currency")) {
            payload.options ||= { currency_code: me._activeCurrency, msrp_currency_code: me._activeMSRPCurrency };
        }

        if (viewAs) {
            payload.items_options.company = viewAs._id;
        }

        // user has manually picked specific items on the page, so there is no need for others
        if (payload.items_options.filters._id?.length) {
            return payload;
        }

        if (inputFilters) {
            const plainInputFilters = inputFilters.toJS();

            delete plainInputFilters.__active;

            if (plainInputFilters.__search) {
                payload.items_options.search = plainInputFilters.__search;
                delete plainInputFilters.__search;
            }

            payload.items_options.filters = {
                ...payload.items_options.filters,
                ...plainInputFilters,
            };
        }

        return payload;
    }

    createItemExport() {
        const data = this.getExportSettings();
        const { fileType } = this.state;
        const { activeBrand } = this.props;

        if (fileType === "orderForm") {
            fetch(`/api/v3.0/brand/${activeBrand._id}/items/export-order`, {
                method: "POST",
                body: JSON.stringify(data),
            })
                .then((res) => res.json())
                .then((data) => {
                    this.setState({
                        exportCreated: true,
                        exportId: data.id,
                    });
                })
                .catch(() => {
                    this.setState({
                        exportFailure: true,
                    });
                });
        } else if (fileType === "atsReport") {
            const payload = this.getAtsExportRequestPayload();
            fetch("/api/v3.0/inventory/export/ats-report", {
                method: "POST",
                body: JSON.stringify(payload),
            })
                .then((res) => {
                    if (!res.ok) {
                        return Promise.reject();
                    }
                    return res.json();
                })
                .then((data) => {
                    this.setState({
                        exportCreated: true,
                        exportId: data.id,
                    });
                })
                .catch(() => {
                    this.setState({
                        exportFailure: true,
                    });
                });
        } else {
            delete data.__currency_code;
            delete data.__msrp_currency_code;

            Ajax.sync("create", new Model(), {
                url: "/api/v3.0/items/export",
                json: data,
                success: (data) => {
                    this.setState({
                        exportCreated: true,
                        exportId: data.id,
                    });
                },
                error: (err) => {
                    this.setState({
                        exportFailure: true,
                    });
                },
            });
        }
    }

    reset() {
        this.setState({
            fileType: "csv",
            view: "vertical",
            archived: false,
            exportCreated: false,
            exportFailure: false,
            exportId: null,
            exportStarted: false,
            images: false,
        });
    }

    handleOrderFormTemplatChange = (e, index, orderFormTemplate) => {
        this.setState({
            orderFormTemplate,
        });
    };

    handleAtsCustomTemplateChange = (e, index, atsCustomTemplate) => {
        this.setState({
            atsCustomTemplate,
        });
    };

    handleATSChange = memoize(
        (key) => (event, value) =>
            this.setState({
                [key]: value,
            })
    );

    renderATSForm() {
        const { fileType, availFromDate, minQuantity, warehouses, selectedWarehouses, excludeBrokenRange, atsOnly } =
            this.state;
        if (fileType !== "orderForm" || this.props.activeBrand.hasPreference("hide_order_form_filters")) {
            return null;
        }
        return (
            <ATSForm
                warehouses={warehouses}
                selectedWarehouses={selectedWarehouses}
                onWarehouseChange={this.handleATSChange("selectedWarehouses")}
                availFromDate={availFromDate}
                onDateChange={this.handleATSChange("availFromDate")}
                minQuantity={minQuantity}
                onQuantityChange={this.handleATSChange("minQuantity")}
                excludeBrokenRange={excludeBrokenRange}
                onExcludeChange={this.handleATSChange("excludeBrokenRange")}
                atsOnly={atsOnly}
                onATSChange={this.handleATSChange("atsOnly")}
            />
        );
    }

    render() {
        const { me, viewAs } = this.context;
        const {
            open,
            onRequestClose,
            bulkItems,
            activeBrand,
            templates = [],
            atsCustomTemplates = [],
            deliveryWindows,
        } = this.props;
        const {
            images,
            fileType,
            view,
            archived,
            exportCreated,
            exportFailure,
            exportId,
            orderFormTemplate,
            atsCustomTemplate,
            exportStarted,
        } = this.state;

        const canViewDisabledItems = me.hasPermission("view_disabled_items");

        const customExports = activeBrand.hasPreference("custom_exports");
        const customAtsExports = activeBrand.hasPreference("custom_ats_exports");
        const hideAtsReportExport = activeBrand.hasPreference("hide_ats_report_export");
        const displayInventory = me.hasPermission("display_inventory");

        if (!open) {
            return null;
        }

        let archivedCheckbox;
        if (
            fileType !== "atsReport" &&
            fileType !== "orderForm" &&
            bulkItems.allSelected &&
            !viewAs &&
            canViewDisabledItems
        ) {
            archivedCheckbox = (
                <Checkbox
                    label={format("Include archived products only")}
                    checked={archived}
                    onCheck={this.handleToggleArchived}
                    style={styles.checkbox}
                />
            );
        }

        const radios = [
            <RadioButton
                key="csv"
                value="csv"
                label={format("Product CSV")}
                style={styles.radio}
                data-test={DATATEST.GALLERY.EXPORT_CSV}
            />,
            <RadioButton
                key="xlsx"
                value="xlsx"
                label={format("Product XLS")}
                style={styles.radio}
                data-test={DATATEST.GALLERY.EXPORT_XLS}
            />,
        ];

        let orderFormSelect;
        if (customExports) {
            radios.push(
                <RadioButton
                    key="order"
                    value="orderForm"
                    label={format("XLS Order Form")}
                    style={styles.radio}
                    data-test={DATATEST.GALLERY.EXPORT_XLS_ORDER_FORM}
                />
            );

            let options;
            if (fileType === "orderForm") {
                if (templates && templates.length) {
                    options = templates
                        .filter((value) => !!value.active)
                        .map(({ id, name }, index) => <MenuItem value={id} primaryText={name} key={`o${index}`} />);
                } else {
                    options = [
                        <MenuItem
                            value="horizontalqty"
                            primaryText={format("Horizontal XLS w_ Size & Qty Headers")}
                            key="o0"
                        />,
                        <MenuItem
                            value="horizontalqty-images"
                            primaryText={format("Horizontal XLS w_ Size & Qty Headers w_ Images")}
                            key="o1"
                        />,
                        <MenuItem
                            value="horizontalsize"
                            primaryText={format("Horizontal XLS w_ Size Headers")}
                            key="o2"
                        />,
                        <MenuItem
                            value="horizontalsize-images"
                            primaryText={format("Horizontal XLS w_ Size Headers w_ Images")}
                            key="o3"
                        />,
                        <MenuItem value="vertical" primaryText={format("Vertical XLS")} key="o4" />,
                        <MenuItem value="vertical-images" primaryText={format("Vertical XLS w_ Images")} key="o5" />,
                    ];

                    if (deliveryWindows.length > 0) {
                        options.push(
                            <MenuItem
                                value="verticaldeliverywindows"
                                primaryText={format("Vertical XLS w_ Delivery Windows")}
                                key="o6"
                            />
                        );
                        options.push(
                            <MenuItem
                                value="verticaldeliverywindows-images"
                                primaryText={format("Vertical XLS w_ Delivery Windows w_ Images")}
                                key="o7"
                            />
                        );
                    }
                    options.push(<MenuItem value="horizontalsimple" primaryText={format("Simple XLS")} key="o8" />);
                }
                orderFormSelect = (
                    <SelectField
                        label={format("XLS Template")}
                        value={orderFormTemplate}
                        onChange={this.handleOrderFormTemplatChange}
                        name="xlsx-template"
                        style={styles.select}
                    >
                        {options}
                    </SelectField>
                );
            }
        }

        let atsTemplateSelect;
        if (displayInventory && customAtsExports && !hideAtsReportExport) {
            radios.push(
                <RadioButton
                    key="ats"
                    value="atsReport"
                    label={format("ATS Report")}
                    style={styles.radio}
                    data-test={DATATEST.GALLERY.EXPORT_ATS_REPORT}
                />
            );

            if (fileType === "atsReport") {
                atsTemplateSelect = (
                    <SelectField
                        label={format("ATS Template")}
                        value={atsCustomTemplate}
                        onChange={this.handleAtsCustomTemplateChange}
                        name="ats-template"
                        style={styles.select}
                    >
                        <MenuItem
                            key={AtsExportPreset.AtsReportCsv}
                            value={AtsExportPreset.AtsReportCsv}
                            primaryText={format("ATS Report CSV")}
                        />
                        <MenuItem
                            key={AtsExportPreset.AtsReportXlsx}
                            value={AtsExportPreset.AtsReportXlsx}
                            primaryText={format("ATS Report XLS")}
                        />
                        <MenuItem
                            key={AtsExportPreset.AtsReportXlsxWithImages}
                            value={AtsExportPreset.AtsReportXlsxWithImages}
                            primaryText={format("ATS Report XLS w/ Images")}
                        />
                        {atsCustomTemplates &&
                            atsCustomTemplates
                                .filter((value) => !!value.active)
                                .map(({ id, name }, index) => (
                                    <MenuItem value={id} primaryText={name} key={`o${index}`} />
                                ))}
                    </SelectField>
                );
            }
        }

        let content = (
            <div>
                <Form>
                    <div style={styles.inputContainer}>
                        <Label> {format("File Format")} </Label>
                        <RadioButtonGroup
                            name="format"
                            valueSelected={fileType}
                            onChange={this.handleRadioChange.bind(this, "fileType")}
                        >
                            {radios}
                        </RadioButtonGroup>{" "}
                    </div>
                    {fileType === "xlsx" && (
                        <div>
                            <Checkbox
                                label={format("Include images")}
                                checked={images}
                                onCheck={this.handleToggleImages}
                                style={styles.checkbox}
                            />
                        </div>
                    )}
                    {["csv", "xlsx"].includes(fileType) && (
                        <div style={styles.inputContainer}>
                            <Label> {format("View")} </Label>
                            <RadioButtonGroup
                                name="view"
                                valueSelected={view}
                                onChange={this.handleRadioChange.bind(this, "view")}
                            >
                                <RadioButton
                                    value="horizontal"
                                    label={format("Horizontal")}
                                    style={styles.radio}
                                    data-test={DATATEST.GALLERY.EXPORT_HORIZONTAL}
                                />
                                <RadioButton
                                    value="vertical"
                                    label={format("Vertical")}
                                    style={styles.radio}
                                    data-test={DATATEST.GALLERY.EXPORT_VERTICAL}
                                />
                            </RadioButtonGroup>{" "}
                        </div>
                    )}
                    {archivedCheckbox} {orderFormSelect}
                    {atsTemplateSelect}
                </Form>
                {this.renderATSForm()}
            </div>
        );

        if (exportCreated) {
            let exportType;
            if (fileType === "orderForm") {
                exportType = ExportType.OrderForm;
            } else if (fileType === "atsReport") {
                exportType = ExportType.AtsReport;
            } else {
                exportType = ExportType.Items;
            }
            content = <ExportInitiationResult exportId={exportId} exportType={exportType} />;
        }

        if (exportFailure) {
            content = (
                <div style={styles.exportFailure}>
                    {" "}
                    {format("There was an error creating this export. Please refresh and try again.")}{" "}
                </div>
            );
        }

        let actions;
        if (!exportFailure && !exportCreated) {
            actions = [
                <Button
                    data-test={DATATEST.GALLERY.EXPORT_ACTION}
                    key="export"
                    label={format("Export")}
                    onClick={this.handleExportButtonClicked}
                    style={styles.export}
                    primary={true}
                    disabled={exportStarted}
                />,
            ];
        }

        return (
            <Dialog
                title={format("Export")}
                autoScrollBodyContent={true}
                contentStyle={styles.content}
                open={open}
                onRequestClose={onRequestClose}
                actions={actions}
            >
                {content}
            </Dialog>
        );
    }
}

export default ExportItems;
